import { isRemoteEnabled } from './utils/remotes';

export function prefetchRemotes() {
  if (typeof window === 'undefined') return;

  const connection: any = (navigator as any).connection;
  const isSlowOrSavingData = !!connection && (connection.saveData || /(^2g$|3g)/i.test(connection.effectiveType));
  if (isSlowOrSavingData) return;

  type MfeMetric = {
    type: 'remoteEntry' | 'import';
    id: string; // container name for remoteEntry, or specifier for import
    url?: string;
    ok: boolean;
    durationMs: number;
    cachedLikely?: boolean;
    timestamp: number;
    error?: string;
  };

  const metricsBucket: MfeMetric[] = ((window as any).__mfeMetrics__ ||= []);
  const metricsEndpoint: string | undefined = (import.meta as any).env?.VITE_MFE_METRICS_URL;
  let beaconScheduled = false;

  const scheduleBeaconFlush = () => {
    if (!metricsEndpoint || beaconScheduled || !('sendBeacon' in navigator)) return;
    beaconScheduled = true;
    const flush = () => {
      try {
        if (metricsBucket.length === 0) return;
        const payload = JSON.stringify({ ts: Date.now(), metrics: metricsBucket.splice(0) });
        (navigator as any).sendBeacon(metricsEndpoint, new Blob([payload], { type: 'application/json' }));
      } catch (_) {
        // ignore
      }
    };
    window.addEventListener('pagehide', flush, { once: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) flush();
    }, { once: true });
  };

  const recordMetric = (m: MfeMetric) => {
    metricsBucket.push(m);
    // Быстрый видимый лог для отладки
    const tag = m.ok ? '✅' : '⛔';
    console.debug(`[MFE ${tag}] ${m.type} ${m.id}`, { durationMs: m.durationMs, cachedLikely: m.cachedLikely, url: m.url, error: m.error });
    scheduleBeaconFlush();
  };

  const detectCachedLikely = (url: string, durationMs: number): boolean => {
    // Грубая эвристика: очень быстрые загрузки обычно из кэша
    if (durationMs < 50) return true;
    try {
      const entries = performance.getEntriesByName(url, 'resource') as PerformanceResourceTiming[];
      const entry = entries && entries[0];
      if (!entry) return false;
      // При отсутствии TAO transferSize=0, но encodedBodySize>0 может указывать на кэш/304
      if ((entry as any).transferSize === 0 && (entry as any).encodedBodySize > 0) return true;
    } catch (_) {}
    return false;
  };

  const injectRemote = (url: string, containerGlobal: string) => {
    try {
      if ((window as any)[containerGlobal]) return;
      const script = document.createElement('script');
      const start = performance.now();
      script.src = url;
      script.type = 'module';
      (script as any).crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => {
        const duration = performance.now() - start;
        recordMetric({
          type: 'remoteEntry',
          id: containerGlobal,
          url,
          ok: true,
          durationMs: Math.round(duration),
          cachedLikely: detectCachedLikely(url, duration),
          timestamp: Date.now()
        });
        // Удаляем ошибку из реестра, если модуль успешно загрузился
        if (typeof window !== 'undefined') {
          const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
          delete errorRegistry[containerGlobal];
        }
      };
      script.onerror = (ev: any) => {
        const duration = performance.now() - start;
        const errorMsg = (ev && ev.message) || 'script error';
        recordMetric({
          type: 'remoteEntry',
          id: containerGlobal,
          url,
          ok: false,
          durationMs: Math.round(duration),
          cachedLikely: false,
          timestamp: Date.now(),
          error: errorMsg
        });
        // Регистрируем ошибку в глобальном хранилище для использования в safeLazyImport
        if (typeof window !== 'undefined') {
          const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
          errorRegistry[containerGlobal] = {
            error: errorMsg,
            timestamp: Date.now(),
            url,
          };
          console.warn(`[prefetchRemotes] Module "${containerGlobal}" failed to load from ${url}. Will use fallback.`);
        }
      };
      document.head.appendChild(script);
    } catch (error) {
      console.debug('prefetch remote failed', containerGlobal, error);
      // Регистрируем ошибку при исключении
      if (typeof window !== 'undefined') {
        const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
        errorRegistry[containerGlobal] = {
          error: String(error),
          timestamp: Date.now(),
          url,
        };
      }
    }
  };

  const measureImport = async (specifier: string) => {
    const start = performance.now();
    try {
      const mod = await import(/* @vite-ignore */ specifier as any);
      const duration = performance.now() - start;
      recordMetric({
        type: 'import',
        id: specifier,
        ok: true,
        durationMs: Math.round(duration),
        timestamp: Date.now()
      });
      // Удаляем ошибку из реестра, если модуль успешно загрузился
      if (typeof window !== 'undefined') {
        const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
        delete errorRegistry[specifier];
      }
      return mod;
    } catch (error: any) {
      const duration = performance.now() - start;
      const errorMsg = String(error?.message || error);
      recordMetric({
        type: 'import',
        id: specifier,
        ok: false,
        durationMs: Math.round(duration),
        timestamp: Date.now(),
        error: errorMsg
      });
      // Регистрируем ошибку в глобальном хранилище
      if (typeof window !== 'undefined') {
        const errorRegistry = (window as any).__mfeLoadErrors__ ||= {};
        errorRegistry[specifier] = {
          error: errorMsg,
          timestamp: Date.now(),
        };
        console.warn(`[prefetchRemotes] Import "${specifier}" failed. Will use fallback.`);
      }
      // Не пробрасываем ошибку дальше, чтобы не блокировать загрузку
      return null;
    }
  };

  const warmImports = async () => {
    // Формы авторизации
    measureImport('auth/LoginForm');
    measureImport('auth/RegisterForm');
    measureImport('auth/EmailVerificationForm');
    measureImport('auth/Notification');

    // Хуки (подгружаем код заранее, но использовать будем по месту)
    measureImport('auth/useAuth');
    measureImport('auth/useAuthStore');
    measureImport('auth/useUIStore');

    // Часто используемые приложения (если есть)
    measureImport('orgStructure/OrgStructureApp');
    measureImport('taxService/TaxServiceApp');
    measureImport('rules/RulesApp');
    measureImport('payroll/PayrollApp');
    measureImport('documentsConstructor/DocumentConstructorApp');
    measureImport('intInvoices/IntInvoicesApp');
    measureImport('dashboardsDataPrep/DashboardsDataPrepApp');
    measureImport('dashboardsDataExport/DashboardsDataExportApp');
    measureImport('dashboardsStyles/DashboardsStylesApp');
    measureImport('bankAccounts/BankAccountsApp');
    measureImport('aggregatorService/AggregatorServiceApp');
    measureImport('filesRecognitionApp/FilesRecognitionApp');
    measureImport('filesRecognitionTemplates/FilesRecognitionTemplates');
    measureImport('integrationHub/IntegrationHubApp');
    measureImport('onecExport/onecExport');
    measureImport('emailFileExtractor/EmailFileExtractorApp');
    measureImport('rulesAiApp/RulesAiApp');
    measureImport('n8nConfigsApp/N8nConfigsApp');
    // Канбан выведен из эксплуатации: доска живёт в armApp.
    // measureImport('kanbanApp/KanbanApp');
    measureImport('armApp/ArmApp');
  };

  const run = () => {
    // Соответствует именам remotes в vite.config.ts
    const env: any = (import.meta as any).env || {};
    const BUILD_ID: string = (env.VITE_BUILD_ID as any) || (typeof Date !== 'undefined' ? String(Date.now()) : '0');
    const withVersion = (u: string) => u.includes('?') ? `${u}&v=${BUILD_ID}` : `${u}?v=${BUILD_ID}`;
    // Пустой URL = remote в этой инсталляции не развёрнут (utils/remotes.ts) — не тянем
    const remote = (value: string | undefined, fallback: string, containerGlobal: string) => {
      if (!isRemoteEnabled(value)) return;
      injectRemote(withVersion(value || fallback), containerGlobal);
    };
    remote(env.VITE_AUTH_MFE_URL, 'https://auth.osno-va.com/remoteEntry.js', 'auth');
    remote(env.VITE_ORGSTRUCTURE_MFE_URL, 'https://org-structure.osno-va.com/remoteEntry.js', 'orgStructure');
    remote(env.VITE_DOCUMENTS_CONSTRUCTOR_MFE_URL, 'https://doc-constructor.osno-va.com/remoteEntry.js', 'documentsConstructor');
    remote(env.VITE_TAX_MFE_URL, 'https://taxes.osno-va.com/remoteEntry.js', 'taxService');
    remote(env.VITE_RULES_MFE_URL, 'https://rules.osno-va.com/remoteEntry.js', 'rules');
    remote(env.VITE_PAYROLL_MFE_URL, 'https://payroll.osno-va.com/remoteEntry.js', 'payroll');
    remote(env.VITE_INT_INVOICES_MFE_URL, 'https://int-invoices.osno-va.com/remoteEntry.js', 'intInvoices');
    remote(env.VITE_DASHBOARDS_DATA_PREP_MFE_URL, 'https://dashboards-data-prep.osno-va.com/remoteEntry.js', 'dashboardsDataPrep');
    remote(env.VITE_DASHBOARDS_DATA_EXPORT_MFE_URL, 'https://dashboards-data-export.osno-va.com/remoteEntry.js', 'dashboardsDataExport');
    remote(env.VITE_DASHBOARDS_STYLES_MFE_URL, 'https://dashboards-styles.osno-va.com/remoteEntry.js', 'dashboardsStyles');
    remote(env.VITE_BANK_ACCOUNTS_MFE_URL, 'https://bank-accounts.osno-va.com/remoteEntry.js', 'bankAccounts');
    remote(env.VITE_AGGREGATOR_SERVICE_MFE_URL, 'https://aggregator-service.osno-va.com/remoteEntry.js', 'aggregatorService');
    remote(env.VITE_FILES_RECOGNITION_MFE_URL, 'https://files-recognition.osno-va.com/remoteEntry.js', 'filesRecognitionApp');
    remote(env.VITE_FILES_RECOGNITION_TEMPLATES_MFE_URL, 'https://files-recognition-templates.osno-va.com/remoteEntry.js', 'filesRecognitionTemplates');
    remote(env.VITE_INTEGRATION_HUB_MFE_URL, 'https://integration-hub.osno-va.com/remoteEntry.js', 'integrationHub');
    remote(env.VITE_ONEC_EXPORT_MFE_URL, 'https://onec-export.osno-va.com/remoteEntry.js', 'onecExport');
    remote(env.VITE_EMAIL_FILE_EXTRACTOR_MFE_URL, 'https://email-file-extractor.osno-va.com/remoteEntry.js', 'emailFileExtractor');
    remote(env.VITE_RULES_AI_MFE_URL, 'https://osnova-rules-ai.osno-va.com/remoteEntry.js', 'rulesAiApp');
    remote(env.VITE_N8N_CONFIGS_MFE_URL, 'https://n8n-configs.osno-va.com/remoteEntry.js', 'n8nConfigsApp');
    // remote(env.VITE_KANBAN_MFE_URL, 'https://kanban.osno-va.com/remoteEntry.js', 'kanbanApp');
    remote(env.VITE_ARM_MFE_URL, 'https://arm.osno-va.com/remoteEntry.js', 'armApp');
    // Разогрев модулей
    warmImports();
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 0);
  }
}


